import {
  fetchWordPressAssetStatus,
  requestWordPressPublicationUnpublish,
} from './service';

import type {
  WordPressAssetStatus,
  WordPressPublicationType,
} from './types';

type WordPressStatusPublication = NonNullable<WordPressAssetStatus['publications']>[number];

export interface WordPressArchiveUnpublishImpact {
  assetId: string;
  targets: WordPressPublicationType[];
}

function publicationNeedsUnpublish(publication: WordPressStatusPublication): boolean {
  if (publication.wordpressAction === 'unpublish' && publication.syncStatus === 'sync_pending') {
    return false;
  }

  const hasRemoteId = String(publication.wordpressRemoteId || '').trim().length > 0;
  return publication.enabled === true
    || publication.wordpressStatus === 'published'
    || publication.wordpressAction === 'publish'
    || publication.syncStatus === 'sync_pending'
    || hasRemoteId;
}

export async function getWordPressArchiveUnpublishImpacts(
  assetIds: string[],
): Promise<WordPressArchiveUnpublishImpact[]> {
  const uniqueAssetIds = Array.from(new Set(assetIds.map(String).filter(Boolean)));
  if (uniqueAssetIds.length === 0) return [];

  const statuses = await Promise.all(
    uniqueAssetIds.map(async (assetId) => ({
      assetId,
      status: await fetchWordPressAssetStatus(assetId),
    })),
  );

  return statuses
    .map(({ assetId, status }) => {
      const targets = (status?.publications || [])
        .filter(publicationNeedsUnpublish)
        .map(publication => publication.target);
      return {
        assetId,
        targets: Array.from(new Set(targets)),
      };
    })
    .filter(impact => impact.targets.length > 0);
}

export async function requestWordPressArchiveUnpublish(
  impacts: WordPressArchiveUnpublishImpact[],
): Promise<void> {
  for (const impact of impacts) {
    for (const target of impact.targets) {
      await requestWordPressPublicationUnpublish(impact.assetId, target);
    }
  }
}

export function getWordPressArchiveUnpublishTargetCount(
  impacts: WordPressArchiveUnpublishImpact[],
): number {
  return impacts.reduce((sum, impact) => sum + impact.targets.length, 0);
}
